import { useState, useEffect } from 'react';
import { CreateRoleData, RoleModule, RolesAPI, RoleSummary, UpdateRoleData } from '@/api/roles';
import { useAuth } from '@/contexts/AuthContext';

export function useRoles() {
  const { workgroupEpoch } = useAuth();
  const [data, setData] = useState<RoleSummary[]>([]);
  const [modules, setModules] = useState<RoleModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await RolesAPI.getAll();
      if (response.code === '200' && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch roles');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await RolesAPI.getModules();
      if (response.code === '200' && response.data) {
        setModules(response.data);
      }
    } catch (err) {
      // Non-fatal -- the roles list itself still loads.
    }
  };

  const createRole = async (data: CreateRoleData) => {
    try {
      const response = await RolesAPI.create(data);
      if (response.code === '200' || response.code === '201') {
        await fetchRoles();
        return { success: true, data: response.data, message: 'Role created successfully' };
      }
      return { success: false, message: response.message || 'Failed to create role' };
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  const updateRole = async (id: number, data: UpdateRoleData) => {
    try {
      const response = await RolesAPI.update(id, data);
      if (response.code === '200') {
        await fetchRoles();
        return { success: true, data: response.data, message: 'Role updated successfully' };
      }
      return { success: false, message: response.message || 'Failed to update role' };
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  const deleteRole = async (id: number) => {
    try {
      const response = await RolesAPI.delete(id);
      if (response.code === '200') {
        await fetchRoles();
        return { success: true, message: 'Role deleted successfully' };
      }
      return { success: false, message: response.message || 'Failed to delete role' };
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchModules();
  }, [workgroupEpoch]);

  return {
    data,
    modules,
    loading,
    error,
    createRole,
    updateRole,
    deleteRole,
    refetch: fetchRoles,
  };
}

export default useRoles;
